import { CalendarClock, ChevronLeft, ChevronRight, Clock3, Wallet } from "lucide-react";

import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardHeader, StatsCard } from "@/components/ui/Card";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import { ApiError } from "@/lib/api";
import { formatDuration } from "@/lib/date";
import { useI18n } from "@/hooks/useI18n";
import { usePayrollSummary } from "./usePayrollSummary";

/** Bulan berjalan `YYYY-MM` — default tampilan payroll. */
function currentMonth(): string {
    return new Date().toISOString().slice(0, 7);
}

/** Geser bulan sebesar `offset` (±1) dari `YYYY-MM`. */
function shiftMonth(month: string, offset: number): string {
    const [year, mon] = month.split("-").map(Number);
    const date = new Date(Date.UTC(year, mon - 1 + offset, 1));
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Label bulan, mis. "September 2026" (mengikuti locale aktif). */
function monthLabel(month: string, locale: string): string {
    const [year, mon] = month.split("-").map(Number);
    return new Intl.DateTimeFormat(locale, {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    }).format(new Date(Date.UTC(year, mon - 1, 1)));
}

function basisLabel(t: (key: string) => string, value: string | null): string {
    if (!value) return "—";
    const key = `payroll.basis.${value}`;
    return t(key) === key ? value : t(key);
}

const CURRENCY = "IDR";

export function PayrollPage() {
    const { t, locale } = useI18n();
    const [month, setMonth] = useState(currentMonth);

    const { data, isPending, isError, error, refetch } = usePayrollSummary(month);

    const currency = useMemo(
        () =>
            new Intl.NumberFormat(locale, {
                style: "currency",
                currency: data?.currency ?? CURRENCY,
                maximumFractionDigits: 0,
            }),
        [locale, data?.currency],
    );

    const isCurrentMonth = month === currentMonth();

    return (
        <div className="flex flex-col gap-6">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="app-text-primary text-2xl font-bold sm:text-3xl">
                        {t("payroll.title")}
                    </h1>
                    <p className="app-shell-text-muted mt-1 text-sm">{t("payroll.subtitle")}</p>
                </div>

                <nav
                    aria-label={t("payroll.month.label")}
                    className="app-content-subtle flex w-fit items-center gap-x-1.5 rounded-2xl border app-shell-border px-2.5 py-2"
                >
                    <button
                        type="button"
                        aria-label={t("payroll.month.prev")}
                        onClick={() => setMonth((prev) => shiftMonth(prev, -1))}
                        className="app-shell-text-muted rounded-lg p-2 transition-colors hover:app-content-subtle-hover hover:text-[var(--app-text-primary)] [&>svg]:h-5 [&>svg]:w-5"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>

                    <span className="app-text-primary w-40 shrink-0 text-center text-sm font-semibold">
                        {monthLabel(month, locale)}
                    </span>

                    <button
                        type="button"
                        aria-label={t("payroll.month.next")}
                        onClick={() => setMonth((prev) => shiftMonth(prev, 1))}
                        disabled={isCurrentMonth}
                        className={
                            isCurrentMonth
                                ? "app-shell-text-muted cursor-not-allowed rounded-lg p-2 opacity-40 [&>svg]:h-5 [&>svg]:w-5"
                                : "app-shell-text-muted rounded-lg p-2 transition-colors hover:app-content-subtle-hover hover:text-[var(--app-text-primary)] [&>svg]:h-5 [&>svg]:w-5"
                        }
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </nav>
            </header>

            {isPending && <LoadingBlock label={t("payroll.loading")} />}

            {isError && (
                <Card>
                    <ErrorState
                        message={
                            error instanceof ApiError ? error.message : t("payroll.loadFailed")
                        }
                        onRetry={() => void refetch()}
                    />
                </Card>
            )}

            {data && data.outlets.length === 0 && (
                <Card>
                    <EmptyState
                        icon={<Wallet className="h-6 w-6" />}
                        title={t("payroll.empty.title")}
                        description={t("payroll.empty.description")}
                    />
                </Card>
            )}

            {data && data.outlets.length > 0 && (
                <>
                    <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <StatsCard
                            label={t("payroll.stats.total")}
                            value={data.totalAmount === null ? "—" : currency.format(data.totalAmount)}
                            icon={<Wallet className="h-5 w-5" />}
                            hint={t("payroll.disclaimer")}
                        />
                        <StatsCard
                            label={t("payroll.stats.sessions")}
                            value={data.eligibleSessions}
                            icon={<CalendarClock className="h-5 w-5" />}
                        />
                        <StatsCard
                            label={t("payroll.stats.hours")}
                            value={formatDuration(data.eligibleMinutes)}
                            icon={<Clock3 className="h-5 w-5" />}
                        />
                    </section>

                    <Card>
                        <CardHeader
                            title={t("payroll.tableTitle")}
                            description={t("payroll.disclaimer")}
                            icon={<Wallet className="h-5 w-5" />}
                        />
                        <CardContent noPadding>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="app-text-soft border-b app-shell-border text-xs uppercase tracking-wide">
                                            <th className="px-5 py-3 font-semibold">
                                                {t("payroll.column.outlet")}
                                            </th>
                                            <th className="px-5 py-3 text-right font-semibold">
                                                {t("payroll.column.sessions")}
                                            </th>
                                            <th className="px-5 py-3 text-right font-semibold">
                                                {t("payroll.column.hours")}
                                            </th>
                                            <th className="px-5 py-3 text-right font-semibold">
                                                {t("payroll.column.rate")}
                                            </th>
                                            <th className="px-5 py-3 font-semibold">
                                                {t("payroll.column.basis")}
                                            </th>
                                            <th className="px-5 py-3 text-right font-semibold">
                                                {t("payroll.column.estimate")}
                                            </th>
                                            <th className="px-5 py-3 font-semibold">
                                                {t("payroll.column.note")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y app-shell-border">
                                        {data.outlets.map((outlet) => (
                                            <tr
                                                key={outlet.outletId}
                                                className="transition-colors hover:app-content-subtle-hover"
                                            >
                                                <td className="app-text-primary whitespace-nowrap px-5 py-4 font-medium">
                                                    {outlet.outletName}
                                                </td>
                                                <td className="app-text-primary px-5 py-4 text-right">
                                                    {outlet.sessions}
                                                </td>
                                                <td className="app-shell-text-muted px-5 py-4 text-right">
                                                    {formatDuration(outlet.minutes)}
                                                </td>
                                                <td className="app-shell-text-muted px-5 py-4 text-right">
                                                    {outlet.rate === null
                                                        ? "—"
                                                        : currency.format(outlet.rate)}
                                                </td>
                                                <td className="app-shell-text-muted px-5 py-4">
                                                    {basisLabel(t, outlet.basis)}
                                                </td>
                                                <td className="app-text-primary px-5 py-4 text-right font-semibold">
                                                    {outlet.amount === null
                                                        ? "—"
                                                        : currency.format(outlet.amount)}
                                                </td>
                                                <td className="px-5 py-4">
                                                    {outlet.note ? (
                                                        <Badge tone="warning">
                                                            {t(`payroll.notes.${outlet.note}`)}
                                                        </Badge>
                                                    ) : (
                                                        <span className="app-text-soft text-xs">
                                                            —
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
